/**
 * Staff profile (design 2B/2C): the person's name and status on top, then
 * three tabs in their context — Profile (identity from Auth, roles, extra
 * permissions, practitioner details via the Clinic personnel PUT), Working
 * hours and Time off (PractitionerAvailability, behind the availability
 * keys). The admin never leaves the person to manage their schedule. This
 * clinic only — nothing about any other clinic is shown or changeable.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Machine, Notice, TextField } from '@/ui/kit';
import {
  FieldGrid,
  FormCard,
  SelectField,
  Span2,
  TabBar,
  TextAreaField,
  Toggle,
} from '@/ui/formkit';
import {
  addDirectPermission,
  assignRole,
  listMembers,
  listRoles,
  permissionCatalog,
  removeDirectPermission,
  removeRole,
} from '@/core/auth/adminApi';
import { fetchPersonnel, upsertPersonnel } from '@/core/clinic/setupApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { memberDisplayName } from './StaffTab';
import { permissionLabel } from './permissionLabels';
import {
  BUFFER_RANGE,
  MAX_PER_DAY_RANGE,
  MAX_PER_WEEK_RANGE,
  integerField,
  integerProblemText,
  type IntegerProblem,
  type IntegerRange,
} from './numericField';
import { SaveErrorNotice, saveErrorOf, type SaveError } from './saveError';
import { TimeOffSection, WorkingHoursSection } from './PractitionerAvailability';

export const MEMBER_TABS = ['profile', 'hours', 'timeoff'] as const;
export type MemberTab = (typeof MEMBER_TABS)[number];
export interface MemberSearch {
  tab?: MemberTab;
}

export function MemberPage() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { uid } = useParams({ strict: false }) as { uid: string };
  const search = useSearch({ strict: false }) as MemberSearch;
  const navigate = useNavigate();
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const queryClient = useQueryClient();
  // Availability tabs are permission-shaped: absent without the read key, read-only without manage.
  const canAvailability = authz.can(PERM.availabilityRead);
  const manageAvailability = authz.can(PERM.availabilityManage);
  const tab: MemberTab = canAvailability && search.tab ? search.tab : 'profile';
  const setTab = (next: MemberTab) =>
    void navigate({
      to: '/clinic-setup/staff/$uid',
      params: { uid },
      search: next === 'profile' ? {} : { tab: next },
      replace: true,
    });

  const membersQuery = useQuery({
    queryKey: ['members', clinicId],
    queryFn: () => listMembers(clinicId),
    staleTime: 60_000,
  });
  const member = membersQuery.data?.find((m) => m.uid === uid);

  const rolesQuery = useQuery({
    queryKey: ['roles', clinicId],
    queryFn: () => listRoles(clinicId),
    staleTime: 60_000,
  });

  const catalogQuery = useQuery({
    queryKey: ['perm-catalog'],
    queryFn: permissionCatalog,
    staleTime: 10 * 60_000,
  });

  const personnelQuery = useQuery({
    queryKey: ['personnel', clinicId, uid],
    queryFn: () => fetchPersonnel(uid),
    retry: false,
  });

  const invalidateMembers = () =>
    void queryClient.invalidateQueries({ queryKey: ['members', clinicId] });

  const roleAdd = useMutation({
    mutationFn: (roleId: string) => assignRole(clinicId, uid, roleId),
    onSuccess: invalidateMembers,
  });
  const roleRemove = useMutation({
    mutationFn: (roleId: string) => removeRole(clinicId, uid, roleId),
    onSuccess: invalidateMembers,
  });
  const grantAdd = useMutation({
    mutationFn: (key: string) => addDirectPermission(clinicId, uid, key),
    onSuccess: invalidateMembers,
  });
  const grantRemove = useMutation({
    mutationFn: (key: string) => removeDirectPermission(clinicId, uid, key),
    onSuccess: invalidateMembers,
  });

  const [pickRole, setPickRole] = useState('');
  const [pickPerm, setPickPerm] = useState('');

  if (!member) {
    return <p className={a.pageSub}>{membersQuery.isPending ? t('common.loading') : '—'}</p>;
  }

  const name = memberDisplayName(member);
  const heldRoleIds = new Set(member.roles.map((r) => r.id));
  const assignableRoles = (rolesQuery.data ?? []).filter(
    (r) => r.active !== false && !heldRoleIds.has(r.id),
  );
  const grantable = (catalogQuery.data ?? []).filter(
    (p) => p.grantPolicy !== 'platform' && !member.directPermissions.includes(p.key),
  );

  return (
    <div style={{ maxInlineSize: 720, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className={a.pageHead} style={{ marginBlockEnd: 0 }}>
        <h1 className={a.pageTitle}>{name}</h1>
        <p className={a.pageSub}>
          {member.admin && <Chip kind="warn">{t('staff.admin')}</Chip>}{' '}
          {member.status === 'suspended' ? (
            <Chip kind="quiet">{t('staff.suspended')}</Chip>
          ) : (
            <Chip kind="ok">{t('staff.active')}</Chip>
          )}
          {personnelQuery.data?.jobTitle && <> · {personnelQuery.data.jobTitle}</>}
        </p>
      </div>

      {canAvailability && (
        <TabBar
          tabs={[
            { id: 'profile', label: t('member.tab.profile') as string },
            { id: 'hours', label: t('member.tab.hours') as string },
            { id: 'timeoff', label: t('member.tab.timeoff') as string },
          ]}
          active={tab}
          onPick={(id) => setTab(id as MemberTab)}
        />
      )}

      {tab === 'hours' && (
        <>
          <p className={a.pageSub}>{t('member.hoursSub')}</p>
          <WorkingHoursSection uid={uid} manage={manageAvailability} />
          <Notice kind="info">{t('hours.adminOnly')}</Notice>
        </>
      )}
      {tab === 'timeoff' && (
        <>
          <p className={a.pageSub}>{t('member.timeOffSub')}</p>
          <TimeOffSection uid={uid} manage={manageAvailability} />
        </>
      )}

      {tab === 'profile' && (
        <>
          {/* Identity — Auth-owned, read-only here */}
          <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
            <div className={a.row}>
              <span className={a.rowKey}>{t('member.identity')}</span>
              <span className={a.rowValue}>
                <Machine>{member.user.phone ?? ''}</Machine>
                {member.user.email ? (
                  <>
                    {' · '}
                    <Machine>{member.user.email}</Machine>
                  </>
                ) : null}
              </span>
            </div>
            <div className={a.row}>
              <span className={a.rowKey}>{t('member.passwordKey')}</span>
              <span className={a.rowValue} style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
                {t('member.passwordBody')}
              </span>
            </div>
            {canAvailability && (
              <div className={a.row}>
                <span className={a.rowKey}>{t('member.hoursLink')}</span>
                <span className={a.rowValue} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Button variant="link" onClick={() => setTab('hours')}>
                    {t('member.tab.hours')}
                  </Button>
                  <Button variant="link" onClick={() => setTab('timeoff')}>
                    {t('member.tab.timeoff')}
                  </Button>
                </span>
              </div>
            )}
          </div>

          {/* Roles */}
          <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
            <div className={a.row}>
              <span className={a.rowKey}>{t('member.rolesCard')}</span>
              <span className={a.rowValue} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {member.roles.map((r) => (
                  <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Chip kind="ok">{r.name}</Chip>
                    <Button variant="link" onClick={() => roleRemove.mutate(r.id)}>
                      {t('common.remove')}
                    </Button>
                  </span>
                ))}
                {member.roles.length === 0 && '—'}
              </span>
            </div>
            <div className={a.row}>
              <span
                className={a.rowValue}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}
              >
                <SelectField
                  label={t('staffNew.role')}
                  value={pickRole}
                  onChange={setPickRole}
                  options={[
                    { value: '', label: '—' },
                    ...assignableRoles.map((r) => ({ value: r.id, label: r.name })),
                  ]}
                />
                <Button
                  variant="quiet"
                  disabled={pickRole === '' || roleAdd.isPending}
                  onClick={() => pickRole && roleAdd.mutate(pickRole)}
                >
                  {t('common.add')}
                </Button>
              </span>
            </div>
          </div>

          {/* Extra permissions — additive only */}
          <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
            <div style={{ padding: '14px 18px 0' }}>
              <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('member.extraTitle')}</strong>
              <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 4 }}>
                {t('member.extraSub')}
              </p>
            </div>
            <div className={a.row}>
              <span className={a.rowValue} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {member.directPermissions.map((p) => (
                  <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {/* ZOY-633: the same readable name the picker offered — never the key. */}
                    <Chip kind="info">
                      {permissionLabel(intl, p, catalogQuery.data)} · {t('member.extraAdded')}
                    </Chip>
                    <Button variant="link" onClick={() => grantRemove.mutate(p)}>
                      {t('common.remove')}
                    </Button>
                  </span>
                ))}
                {member.directPermissions.length === 0 && '—'}
              </span>
            </div>
            <div className={a.row}>
              <span
                className={a.rowValue}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}
              >
                <SelectField
                  label={t('roles.permissions')}
                  value={pickPerm}
                  onChange={setPickPerm}
                  options={[
                    { value: '', label: '—' },
                    ...grantable.map((p) => ({
                      value: p.key,
                      label: permissionLabel(intl, p.key, catalogQuery.data),
                    })),
                  ]}
                />
                <Button
                  variant="quiet"
                  disabled={pickPerm === '' || grantAdd.isPending}
                  onClick={() => pickPerm && grantAdd.mutate(pickPerm)}
                >
                  {t('common.add')}
                </Button>
              </span>
            </div>
          </div>

          <PractitionerCard uid={uid} personnel={personnelQuery.data ?? null} clinicId={clinicId} />
          <Notice kind="info">{t('member.onlyClinic')}</Notice>
        </>
      )}
    </div>
  );
}

function PractitionerCard(props: {
  uid: string;
  clinicId: string;
  personnel: Awaited<ReturnType<typeof fetchPersonnel>> | null;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const queryClient = useQueryClient();
  const p = props.personnel?.practitioner ?? null;

  const [jobTitle, setJobTitle] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [license, setLicense] = useState('');
  const [experience, setExperience] = useState('');
  const [bio, setBio] = useState('');
  const [qualified, setQualified] = useState(false);
  const [buffer, setBuffer] = useState('');
  const [maxDay, setMaxDay] = useState('');
  const [maxWeek, setMaxWeek] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);

  // ZOY-637: the three numeric fields are validated in words before anything
  // is sent. Clearing a stored value sends null (the backend unsets it); an
  // empty field that was already empty is left out, so the audit never
  // records a change that did not happen.
  const clearOrOmit = (stored: number | null | undefined, v: number | null) =>
    v ?? (stored !== null && stored !== undefined ? null : undefined);
  const bufferField = integerField(buffer, BUFFER_RANGE);
  const maxDayField = integerField(maxDay, MAX_PER_DAY_RANGE);
  const maxWeekField = integerField(maxWeek, MAX_PER_WEEK_RANGE);
  const problemText = (f: { problem: IntegerProblem | null }, range: IntegerRange) =>
    f.problem !== null ? integerProblemText(intl, f.problem, range) : undefined;
  const bufferError = problemText(bufferField, BUFFER_RANGE);
  const maxDayError = problemText(maxDayField, MAX_PER_DAY_RANGE);
  const maxWeekError = problemText(maxWeekField, MAX_PER_WEEK_RANGE);
  const fieldProblem =
    bufferError !== undefined || maxDayError !== undefined || maxWeekError !== undefined;

  useEffect(() => {
    setJobTitle(props.personnel?.jobTitle ?? '');
    setSpecialty(p?.specialty ?? '');
    setLicense(p?.licenseNumber ?? '');
    setExperience(p?.experience ?? '');
    setBio(p?.bio ?? '');
    setQualified(p?.qualifiedToAssess ?? false);
    setBuffer(
      p?.bufferMinutes !== undefined && p?.bufferMinutes !== null ? String(p.bufferMinutes) : '',
    );
    setMaxDay(
      p?.maxSessionsPerDay !== undefined && p?.maxSessionsPerDay !== null
        ? String(p.maxSessionsPerDay)
        : '',
    );
    setMaxWeek(
      p?.maxSessionsPerWeek !== undefined && p?.maxSessionsPerWeek !== null
        ? String(p.maxSessionsPerWeek)
        : '',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.personnel]);

  const save = useMutation({
    mutationFn: () =>
      upsertPersonnel(props.uid, {
        jobTitle: jobTitle || undefined,
        practitioner: {
          specialty: specialty || undefined,
          licenseNumber: license || undefined,
          experience: experience || undefined,
          bio: bio || undefined,
          qualifiedToAssess: qualified,
          bufferMinutes: clearOrOmit(p?.bufferMinutes, bufferField.value),
          maxSessionsPerDay: clearOrOmit(p?.maxSessionsPerDay, maxDayField.value),
          maxSessionsPerWeek: clearOrOmit(p?.maxSessionsPerWeek, maxWeekField.value),
        },
      }),
    onMutate: () => setError(null),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      void queryClient.invalidateQueries({ queryKey: ['personnel', props.clinicId, props.uid] });
    },
    onError: (err) => setError(saveErrorOf(err)),
  });

  const opt = { word: t('signin.optional'), kind: 'optional' as const };

  return (
    <FormCard
      title={t('member.practitioner')}
      sub={t('member.practitionerSub')}
      onSubmit={() => save.mutate()}
      cta={t('common.save')}
      ctaDisabled={fieldProblem}
      ctaDisabledReason={fieldProblem ? (t('field.fixFirst') as string) : undefined}
      busy={save.isPending}
      footNote={saved ? (t('common.saved') as string) : undefined}
    >
      {error && <SaveErrorNotice error={error} />}
      <FieldGrid>
        <TextField label={t('member.jobTitle')} value={jobTitle} onChange={setJobTitle} />
        <TextField label={t('member.specialty')} value={specialty} onChange={setSpecialty} />
        <TextField
          label={t('member.license')}
          value={license}
          onChange={setLicense}
          requirement={opt}
          machine
        />
        <TextField
          label={t('member.experience')}
          value={experience}
          onChange={setExperience}
          requirement={opt}
        />
        <Span2>
          <TextAreaField
            label={t('member.bio')}
            value={bio}
            onChange={setBio}
            requirement={opt}
            rows={2}
          />
        </Span2>
        <Span2>
          <Toggle
            label={t('member.qualified')}
            help={t('member.qualifiedHelp')}
            checked={qualified}
            onChange={setQualified}
            stateWord={
              qualified ? (t('common.enabled') as string) : (t('common.disabled') as string)
            }
          />
        </Span2>
        <TextField
          label={t('member.buffer')}
          value={buffer}
          onChange={setBuffer}
          help={t('member.bufferHelp')}
          error={bufferError}
          requirement={opt}
          inputMode="numeric"
          machine
        />
        <TextField
          label={t('member.maxDay')}
          value={maxDay}
          onChange={setMaxDay}
          error={maxDayError}
          requirement={opt}
          inputMode="numeric"
          machine
        />
        <TextField
          label={t('member.maxWeek')}
          value={maxWeek}
          onChange={setMaxWeek}
          error={maxWeekError}
          requirement={opt}
          inputMode="numeric"
          machine
        />
      </FieldGrid>
    </FormCard>
  );
}
